import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface TourContextType {
  isTourActive: boolean;
  currentStep: number;
  startTour: () => void;
  stopTour: () => void;
  completeTour: () => Promise<void>;
  skipTour: () => Promise<void>;
  setCurrentStep: (step: number) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const startTour = () => {
    setIsTourActive(true);
    setCurrentStep(0);
  };

  const stopTour = () => {
    setIsTourActive(false);
    setCurrentStep(0);
  };

  const completeTour = async () => {
    setIsTourActive(false);
    setCurrentStep(0);

    // Save tour completion to Auth0 user_metadata
    if (!user?.sub) return;

    try {
      const token = await getAccessTokenSilently();
      const apiUrl = import.meta.env.VITE_API_URL || 'https://backend.veltus.io';

      await fetch(`${apiUrl}/auth/user-metadata/${user.sub}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tour_completed: true,
          tour_completed_at: new Date().toISOString(),
        }),
      });

      console.log('Tour completion saved to Auth0 user metadata');
    } catch (error) {
      console.error('Error saving tour completion to Auth0:', error);
    }
  };

  const skipTour = async () => {
    setIsTourActive(false);
    setCurrentStep(0);

    // Save skip status to Auth0 user_metadata
    if (!user?.sub) return;

    try {
      const token = await getAccessTokenSilently();
      const apiUrl = import.meta.env.VITE_API_URL || 'https://backend.veltus.io';

      await fetch(`${apiUrl}/auth/user-metadata/${user.sub}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tour_completed: true,
          tour_skipped: true,
          tour_skipped_at: new Date().toISOString(),
        }),
      });

      console.log('Tour skip saved to Auth0 user metadata');
    } catch (error) {
      console.error('Error saving tour skip to Auth0:', error);
    }
  };

  return (
    <TourContext.Provider
      value={{
        isTourActive,
        currentStep,
        startTour,
        stopTour,
        completeTour,
        skipTour,
        setCurrentStep,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
